/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorModel, OptionType } from '@/shared/api/BaseAPI.model'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { t } from 'i18next'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import {
  menuTypePayloadModel,
  menuTypePutPayloadModel,
  menuTypesResponseModel,
  QUERY
} from '../api/SidebarAccordionAPI.model'
import SidebarAccordionAPI from '../api/SidebarAccordionAPI'
import { useSelectedLanguagesQuery } from './useSelectedLanguagesQuery'

export const useMenuTypesQuery = (menuTypeId?: string) => {
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguage } = useSelectedLanguagesQuery(currentLanguage)

  const menuTypesQuery = useQuery({
    queryKey: [QUERY.MENU_TYPES],
    queryFn: SidebarAccordionAPI.getMenuTypes
  })

  const menuTypesOptions = useMemo(
    () =>
      menuTypesQuery.data?.map(type => ({
        label: type.translations.find(
          t => t.languageCode === defaultLanguage?.languageCode
        )?.name,
        value: type.id
      })) || [],
    [menuTypesQuery.data, defaultLanguage?.languageCode]
  ) as OptionType[]

  const menuTypesList = useQuery<menuTypesResponseModel, ErrorModel>({
    queryKey: [QUERY.MENU_TYPES],
    queryFn: SidebarAccordionAPI.getMenuTypes
  })

  const { mutate: putMenuType } = useMutation<
    menuTypePutPayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (menuTypePayload: menuTypePutPayloadModel) =>
      SidebarAccordionAPI.putMenuType(
        menuTypePayload.menuTypeId,
        menuTypePayload.menuTypePayload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.MENU_TYPES] })
      // The single-item query has its own key, so invalidating the list leaves
      // it stale and the edit form reopens on the pre-save values. Removed
      // rather than invalidated: the query client sets `refetchOnMount: false`,
      // so a stale entry would still be served as-is to the next form.
      queryClient.removeQueries({ queryKey: [QUERY.MENU_TYPE] })
      showSnackbar(
        t('ALERTS.PUT_MENU_TYPE_SUCCESS.TITLE'),
        t('ALERTS.PUT_MENU_TYPE_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.PUT_MENU_TYPE_ERROR.TITLE'),
        t('ALERTS.PUT_MENU_TYPE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: postMenuType } = useMutation<
    menuTypePayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (menuTypePayload: menuTypePayloadModel) =>
      SidebarAccordionAPI.postMenuType(menuTypePayload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.MENU_TYPES] })
      // The single-item query has its own key, so invalidating the list leaves
      // it stale and the edit form reopens on the pre-save values. Removed
      // rather than invalidated: the query client sets `refetchOnMount: false`,
      // so a stale entry would still be served as-is to the next form.
      queryClient.removeQueries({ queryKey: [QUERY.MENU_TYPE] })
      showSnackbar(
        t('ALERTS.POST_MENU_TYPE_SUCCESS.TITLE'),
        t('ALERTS.POST_MENU_TYPE_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_MENU_TYPE_ERROR.TITLE'),
        t('ALERTS.POST_MENU_TYPE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteMenuType } = useMutation<string, ErrorModel, any>({
    mutationFn: (menuTypeId: string) =>
      SidebarAccordionAPI.deleteMenuType(menuTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.MENU_TYPES] })
      // The single-item query has its own key, so invalidating the list leaves
      // it stale and the edit form reopens on the pre-save values. Removed
      // rather than invalidated: the query client sets `refetchOnMount: false`,
      // so a stale entry would still be served as-is to the next form.
      queryClient.removeQueries({ queryKey: [QUERY.MENU_TYPE] })
      showSnackbar(
        t('ALERTS.DELETE_MENU_TYPES_SUCCESS.TITLE'),
        t('ALERTS.DELETE_MENU_TYPES_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_MENU_TYPES_ERROR.TITLE'),
        t('ALERTS.DELETE_MENU_TYPES_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const menuType = useQuery({
    queryKey: [QUERY.MENU_TYPE, menuTypeId],
    queryFn: () => SidebarAccordionAPI.getMenuType(menuTypeId!),
    enabled: !!menuTypeId
  })

  return {
    menuTypesOptions,
    menuTypesList,
    menuTypesQuery,
    putMenuType,
    postMenuType,
    deleteMenuType,
    menuType
  }
}
